import {AfterViewInit, Component, ElementRef, HostListener, Input, OnInit, ViewChild} from '@angular/core';
import {FormModel} from "../form/form.model.abstract";
import {UntypedFormGroup} from "@angular/forms";
import {ElementService} from "../services/element.service";
import {FormModelDirection} from "../form/form.model.direction";

/**
 * Component to select the direction of a house
 */
@Component({
  selector: '[app-formfield-direction]',
  templateUrl: './formfield.direction.component.html'
})
export class FormfieldDirectionComponent implements AfterViewInit, OnInit {
  /**
   * Formmodel of the value
   */
  @Input() formModel: FormModel;

  /**
   * form
   */
  @Input() form: UntypedFormGroup;

  /**
   * current step
   */
  @Input() step: string;

  /**
   * toggle to show the panels on the full roof
   */
  @Input() roofType: string = FormModelDirection.ROOF_TYPE_HALF;

  /**
   * Initial value for the element.
   */
  readonly initialValue = 0;

  /**
   * Directions as text
   */
  readonly directions = [
    "S", "SSO", "SO", "OSO",
    "O", "ONO", "NO", "NNO",
    "N", "NNW", "NW", "WNW",
    "W", "WSW", "SW", "SSW"
  ];

  /**
   * holds the current direction
   */
  direction: string;

  /**
   * current selected number
   */
  value: number;

  /**
   * Some DOM elements that we need for the interaction
   */
  @ViewChild('thumbWrapper') thumbWrapper: ElementRef;
  @ViewChild('thumb') thumb: ElementRef;
  @ViewChild('thumbArrow') thumbArrow: ElementRef;
  @ViewChild('svg') svgElement: ElementRef;
  @ViewChild('circle') pathCircle: ElementRef;
  @ViewChild('building') building: ElementRef;
  @ViewChild('degrees') degrees: ElementRef;

  /**
   * toggle if the thumb is currently dragged
   */
  drag: boolean = false;

  /**
   * Center of the building.
   */
  center = { x: 0, y: 0 };

  /**
   * Radius of the movement.
   */
  radius = 0;

  /**
   * toggle if we have a east-wes-direction
   */
  eastWest: boolean = false;

  /**
   * toggle to show the full roof
   */
  fullRoof: boolean = false;

  /**
   * toggle for showing an extra hint on the roof flat side
   */
  showPentHint: boolean = false;

  /**
   * constructor
   * @param elementService
   */
  constructor(private elementService: ElementService) {}

  /**
   * Check if a given degrees number is interpretet as east/west
   * (only form some rooftypes, they are not checked here)
   * @param value
   */
  static isEastWest(value: number): boolean {
    return (value <= -80 && value >= -100) || (value >= 80 && value <= 100);
  }

  /**
   * @inheritdoc
   */
  ngOnInit() {
    this.fullRoof = this.roofType !== FormModelDirection.ROOF_TYPE_HALF;
    this.showPentHint = this.roofType === FormModelDirection.ROOF_TYPE_PENT;
  }

  /**
   * @inheritdoc
   */
  ngAfterViewInit() {
    // init the center and the radius. Must be made after the DOM is available.
    const circle = this.pathCircle.nativeElement;
    this.center = {
      x: parseFloat(circle.getAttribute('cx')),
      y: parseFloat(circle.getAttribute('cy'))
    };
    this.radius = parseFloat(circle.getAttribute('r'));

    // do we have a preseeted value?
    let currentValue = this.form.get(this.formModel.id)?.value;
    if(currentValue) {
      // preset the element to a given value
      setTimeout(() => {
        if (currentValue < -180) {
          currentValue = -90 - currentValue - 270;
        } else {
          currentValue = -currentValue;
        }

        this.updatePositionAndRotation((currentValue + 90) * Math.PI / 180);
      });
    }
    else {
      // set initial value
      setTimeout(() => this.updateValue(this.initialValue), 0);
    }
  }

  /**
   * Handling the drag event of the thumb
   * @param event
   */
  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  onMouseMove(event: any) {
    if (this.drag === false) {
      // Only continue if mouse button is down
      return;
    }

    // avoind scrolling while dragging
    event.preventDefault();

    // avoid scrolling on mobile devices
    const touch = event.touches ? event.touches[0] : event;
    const rect = this.svgElement.nativeElement.getBoundingClientRect();
    const dx = touch.clientX - rect.left - this.center.x;
    const dy = touch.clientY - rect.top - this.center.y;
    const angle = Math.atan2(dy, dx);
    this.updatePositionAndRotation(angle);
  }

  /**
   * Update the elements, based on a given angle.
   * @param angle
   */
  updatePositionAndRotation(angle: number): void {
    const x = this.center.x + this.radius * Math.cos(angle);
    const y = this.center.y + this.radius * Math.sin(angle);

    let degrees = (angle * 180 / Math.PI) - 90;
    degrees = Math.round(degrees / 5) * 5;
    const posX = x - this.thumb.nativeElement.getAttribute('cx');
    const posY = y - this.thumb.nativeElement.getAttribute('cy');
    // move thumb
    this.thumbWrapper.nativeElement.setAttribute('transform', `translate(${posX}, ${posY})`);
    // rotate arrow
    this.thumbArrow.nativeElement.setAttribute('transform', `rotate(${degrees + 30}, 0, 0) translate(-10, 5)`);
    // rotate building
    this.building.nativeElement.setAttribute('transform', `translate(200,200) rotate(${degrees})`);

    // set the value to the form
    this.updateValue(degrees);
  }

  /**
   * Get the value, based on the movement and set it into the formfield
   * @param value
   */
  updateValue(value: number): void {
    // calculate the correct value
    this.value = Math.round(value);
    if(value < -180) {
      this.value = -90 - (270 + value);
    }
    else {
      this.value = -value;
    }

    // update direction
    this.updateDirection(this.value);

    if (this.value >= -100 && this.value <= 100) {
      // we have a valid value
      this.form.get(this.formModel.id).setValue(this.value);
    }
    else {
      // value is invalid, delete from form to invalidate form
      this.form.get(this.formModel.id).setValue(null);
    }

    // check if we have an east-west-direction
    this.eastWest = FormfieldDirectionComponent.isEastWest(this.value);
  }

  /**
   * Update the direction, based on the currect degrees.
   * @param degrees
   */
  updateDirection(degrees: number): void {
    // normalize degrees to 0-360
    degrees = degrees % 360;

    // fid index, based on degrees
    let index = Math.floor((degrees + 11.25) / 22.5);
    if(index < 0) {
      index = 15 + index + 1;
    }

    // update direction in component and form
    this.direction = this.directions[index];
    if(FormfieldDirectionComponent.isEastWest(this.value) && this.roofType === FormModelDirection.ROOF_TYPE_HALF) {
      this.direction = 'OW';
    }
    this.form.get(this.formModel.fieldData.secondId).setValue(this.direction);
  }

  /**
   * Event when the drag is started
   */
  startDrag() {
    this.drag = true;

    // scroll upwards a little bit, to avoind jumping if at the end of the screen.
    this.elementService.scrollToOffset(-10);
  }

  /**
   * Avoid other events while dragging the thumb.
   * @param event
   */
  onDrag(event: Event): void {
    if(this.drag) {
      event.preventDefault();
    }
  }

  /**
   * Eventhandler for stopping the drag.
   */
  @HostListener('document:mouseup', [])
  @HostListener('document:touchend', [])
  @HostListener('document:keyup', [])
  stopDrag() {
    this.drag = false;
  }

  /**
   * Handle keyboard navigation for the thumbWrapper
   * @param event KeyboardEvent
   */
  onKeyDown(event: KeyboardEvent): void {
    // Prevent default scrolling behavior
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown' ||
        event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();

      // Calculate the current angle in radians
      let degrees = -this.value;
      if (degrees < -180) {
        degrees = -90 - (degrees + 270);
      }
      let angle = (degrees + 90) * Math.PI / 180;

      // Adjust the angle based on arrow key
      const step = 5 * Math.PI / 180; // 5 degrees in radians
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        // Decrease value (counter-clockwise)
        angle -= step;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        // Increase value (clockwise)
        angle += step;
      }

      // Update the position and rotation
      this.updatePositionAndRotation(angle);
    }
  }
}
